import { defineStore } from 'pinia';
import { reactive, ref } from 'vue';

import { parsePhoneNumber } from 'libphonenumber-js';
import moment from 'moment';

import { CONVENTIONAL_DATE_FORMAT, toCalendarDayUtcIso } from '@/helpers/date';
import { isValidPhone } from '@/helpers/phone';
import {
	CandidateFieldType,
	isValidCandidateField,
} from '@/modules/worker/helpers/candidateFormValidation';
import documentService from '@/modules/worker/services/documentService';
import {
	DuplicateResolutionAction,
	DuplicateScenario,
} from '@/modules/worker/services/types';
import workerService from '@/modules/worker/services/workerService';

import type { ParsedResume } from '@/modules/worker/services/documentTypes';
import type { LabelValue } from '@/types/common';
import type { IJobStored } from '@/types/job';
import type { CommonWorker } from '@/types/worker';

type FormField<T> = { value: T; isValid: boolean };

type FindWorkerForm = {
	firstname: FormField<string>;
	lastname: FormField<string>;
	birthDate: FormField<string>;
	email: FormField<string>;
	phone: FormField<string>;
	origin: FormField<LabelValue | null>;
	jobs: FormField<IJobStored[]>;
};

const createInitialFindWorkerForm = (): FindWorkerForm => ({
	firstname: { value: '', isValid: true },
	lastname: { value: '', isValid: true },
	birthDate: { value: '', isValid: true },
	email: { value: '', isValid: true },
	phone: { value: '', isValid: true },
	origin: { value: null, isValid: true },
	jobs: { value: [], isValid: true },
});

const buildDuplicatedWorkerFromForm = (
	workerId: string,
	findWorkerForm: FindWorkerForm
): CommonWorker => ({
	id: workerId,
	firstname: findWorkerForm.firstname.value,
	lastname: findWorkerForm.lastname.value,
	email: findWorkerForm.email.value || '',
	phone: findWorkerForm.phone.value || '',
	birthDate: findWorkerForm.birthDate.value
		? new Date(findWorkerForm.birthDate.value).getTime()
		: 0,
	birthPlace: '',
	nir: '',
	nirKey: '',
	nationalityCode: '',
	birthCountry: '',
	picture: '',
	address: {
		city: '',
		citySlug: '',
		countryCode: '',
	} as CommonWorker['address'],
	agencies: [],
	databaseOrigin: 'interim' as CommonWorker['databaseOrigin'],
	jobs: [],
	source: '',
	jobName: '',
	jobOfferName: '',
	duration: 0,
	matchReasons: [],
	updateProfileData: false,
	scenario: DuplicateScenario.PERFECT_MATCH,
	action: DuplicateResolutionAction.PROFILE_LINK,
});

const applyCvParsingToForm = (
	findWorkerForm: FindWorkerForm,
	cvParsing: ParsedResume
) => {
	findWorkerForm.firstname.value = cvParsing.data?.first_name ?? '';
	findWorkerForm.lastname.value = cvParsing.data?.last_name ?? '';
	findWorkerForm.email.value = cvParsing.data?.email ?? '';
	findWorkerForm.phone.value =
		cvParsing.data?.phone_number &&
		isValidPhone(cvParsing.data.phone_number.toString())
			? parsePhoneNumber(
					cvParsing.data.phone_number.toString(),
					'FR'
				).formatNational()
			: '';
	findWorkerForm.birthDate.value = cvParsing.data?.birthdate_timestamp
		? moment(cvParsing.data.birthdate_timestamp * 1000).format(
				CONVENTIONAL_DATE_FORMAT
			)
		: '';

	findWorkerForm.firstname.isValid = isValidCandidateField(
		findWorkerForm.firstname.value,
		CandidateFieldType.String
	);
	findWorkerForm.lastname.isValid = isValidCandidateField(
		findWorkerForm.lastname.value,
		CandidateFieldType.String
	);
	findWorkerForm.email.isValid = isValidCandidateField(
		findWorkerForm.email.value,
		CandidateFieldType.Email
	);
	findWorkerForm.phone.isValid = isValidCandidateField(
		findWorkerForm.phone.value,
		CandidateFieldType.Phone
	);
	findWorkerForm.birthDate.isValid = isValidCandidateField(
		findWorkerForm.birthDate.value,
		CandidateFieldType.Date
	);
};

export const useWorkerAddIdentificationStore = defineStore(
	'workerAddIdentification',
	() => {
		const loading = ref(false);
		const findWorkerForm = reactive(createInitialFindWorkerForm());
		const duplicatedWorkers = ref<CommonWorker[]>([]);
		const cvParsing = ref<ParsedResume | null>(null);
		const cv = ref('');
		const addToRegistrationsTracking = ref(true);

		const reset = () => {
			loading.value = false;
			Object.assign(findWorkerForm, createInitialFindWorkerForm());
			duplicatedWorkers.value = [];
			cvParsing.value = null;
			cv.value = '';
			addToRegistrationsTracking.value = true;
		};

		const fetchDuplicatedWorkers = async (): Promise<CommonWorker | null> => {
			try {
				loading.value = true;
				const result = await workerService.searchDuplicates({
					firstname: findWorkerForm.firstname.value,
					lastname: findWorkerForm.lastname.value,
					email: findWorkerForm.email.value || undefined,
					phone: findWorkerForm.phone.value || undefined,
					birthDate: toCalendarDayUtcIso(findWorkerForm.birthDate.value),
				});
				if (!result.length) {
					duplicatedWorkers.value = [];
					return null;
				}

				const { id, action, updateProfileData, matchReasons } = result[0];
				if (
					action === DuplicateResolutionAction.AUTO_MERGE &&
					updateProfileData
				) {
					const autoMergeFields = [
						'firstname',
						'lastname',
						'email',
						'phone',
					] as const;
					const fieldsToUpdate = autoMergeFields.filter(
						(field) => !matchReasons.includes(field)
					);
					if (fieldsToUpdate.length > 0) {
						const payload = {
							id,
							...fieldsToUpdate.reduce<Record<string, unknown>>(
								(acc, field) => {
									acc[field] = findWorkerForm[field].value;
									return acc;
								},
								{}
							),
						};
						await workerService.editInterim(payload);
					}
				} else {
					duplicatedWorkers.value = result;
				}
				return result[0];
			} finally {
				loading.value = false;
			}
		};

		const setDuplicatedWorkerFromForm = (workerId: string) => {
			duplicatedWorkers.value = [
				buildDuplicatedWorkerFromForm(workerId, findWorkerForm),
			];
		};

		const fetchCvParsing = async (fileBase64: string) => {
			try {
				loading.value = true;
				cv.value = fileBase64;
				cvParsing.value = await documentService.parseResume({
					fileBase64,
				});
				if (cvParsing.value) {
					applyCvParsingToForm(findWorkerForm, cvParsing.value);
				}
			} finally {
				loading.value = false;
			}
		};

		return {
			loading,
			findWorkerForm,
			duplicatedWorkers,
			cvParsing,
			cv,
			addToRegistrationsTracking,
			reset,
			fetchDuplicatedWorkers,
			setDuplicatedWorkerFromForm,
			fetchCvParsing,
		};
	}
);
